import React, { useContext, useEffect, useState } from "react";
import { StudentContext } from "./StudentLayout";
import { collection, query, where, getDocs } from "firebase/firestore";
import { db } from "../../firebase";
import { Award, Download, Lock, ExternalLink, ShieldCheck } from "lucide-react";

const StudentCertificate = () => {
  const { student } = useContext(StudentContext);
  const [cert, setCert] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      if (!student?.studentId) return;
      try {
        const q = query(collection(db, "certificates"), where("studentId", "==", student.studentId));
        const snap = await getDocs(q);
        if (!snap.empty) setCert({ id: snap.docs[0].id, ...snap.docs[0].data() });
      } catch (e) { console.error(e); }
      setLoading(false);
    };
    fetch();
  }, [student]);

  if (!student) return <div className="text-slate-500">Loading...</div>;

  const eligible = student.paymentStatus === "paid" && (student.attendancePercentage || 0) >= 75 && (student.progress || 0) >= 100;

  return (
    <div data-testid="student-certificate">
      <div className="mb-8">
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-blue-600 mb-2">Certificate</div>
        <h1 className="font-heading text-3xl font-bold tracking-tight text-slate-900">Your Certificate</h1>
        <p className="mt-1 text-slate-600">Verifiable digital certificate for your internship.</p>
      </div>

      {loading ? (
        <div className="text-slate-500">Loading...</div>
      ) : cert ? (
        <div className="bg-gradient-to-br from-blue-50 to-sky-50 border border-blue-200 rounded-2xl p-8">
          <div className="flex items-start gap-4 mb-6">
            <div className="w-14 h-14 rounded-xl bg-blue-600 flex items-center justify-center">
              <Award className="w-7 h-7 text-white" />
            </div>
            <div className="flex-1">
              <h2 className="font-heading text-2xl font-bold text-slate-900">Certificate of Completion</h2>
              <p className="text-slate-600 mt-1">{cert.courseTitle} · {cert.duration}</p>
            </div>
            <span className="px-3 py-1 rounded-full bg-green-600 text-white text-xs font-semibold">Issued</span>
          </div>

          <div className="grid md:grid-cols-2 gap-4 bg-white rounded-xl p-5 border border-blue-100 text-sm">
            <Detail label="Certificate ID" value={cert.certificateId} mono />
            <Detail label="Issue Date" value={cert.issueDate} />
            <Detail label="Student Name" value={cert.studentName} />
            <Detail label="Issued By" value={cert.issuedBy} />
          </div>

          <div className="mt-6 flex flex-wrap gap-3">
            {cert.certificateUrl && (
              <a
                href={cert.certificateUrl}
                target="_blank"
                rel="noreferrer"
                data-testid="cert-download-btn"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-semibold transition-all"
              >
                <Download className="w-4 h-4" /> Download Certificate
              </a>
            )}
            <a
              href={`/verify/${cert.certificateId}`}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-lg border border-blue-300 text-blue-700 hover:bg-blue-50 font-semibold transition-all"
            >
              <ShieldCheck className="w-4 h-4" /> Verify Online <ExternalLink className="w-3 h-3" />
            </a>
          </div>
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-2xl p-10 text-center" data-testid="cert-locked">
          <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
            <Lock className="w-8 h-8 text-slate-400" />
          </div>
          <h2 className="font-heading text-xl font-bold text-slate-900">Certificate Locked</h2>
          <p className="text-slate-600 mt-2 max-w-md mx-auto">
            Your certificate will be available once you meet all completion criteria and admin approves it.
          </p>
          <div className="mt-6 max-w-md mx-auto text-left space-y-2 text-sm">
            <Requirement done={student.paymentStatus === "paid"} text="Payment completed" />
            <Requirement done={(student.attendancePercentage || 0) >= 75} text={`Attendance ≥ 75% (currently ${student.attendancePercentage || 0}%)`} />
            <Requirement done={(student.progress || 0) >= 100} text={`Course progress complete (currently ${student.progress || 0}%)`} />
            <Requirement done={eligible} text="Awaiting admin approval" />
          </div>
        </div>
      )}
    </div>
  );
};

const Detail = ({ label, value, mono }) => (
  <div>
    <div className="text-xs text-slate-500">{label}</div>
    <div className={`font-semibold text-slate-900 mt-0.5 ${mono ? "font-mono" : ""}`}>{value || "—"}</div>
  </div>
);

const Requirement = ({ done, text }) => (
  <div className="flex items-center gap-2">
    <div className={`w-4 h-4 rounded-full flex items-center justify-center ${done ? "bg-green-600 text-white" : "bg-slate-200"}`}>
      {done && <span className="text-xs">✓</span>}
    </div>
    <span className={done ? "text-slate-700" : "text-slate-500"}>{text}</span>
  </div>
);

export default StudentCertificate;
